import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { User, ApiResponse, PaginatedResponse } from 'types';
import { mockUsers } from 'mocks/mockServer';

const useMock = process.env.REACT_APP_USE_MOCK === 'true';

console.log('👥 Users Service initialized - Using Mock:', useMock);

export const usersService = {
  async getUsers(params?: any): Promise<ApiResponse<PaginatedResponse<User>>> {
    if (useMock) {
      console.log('👥 Using MOCK data for users');
      return mockUsers.getUsers(params);
    }

    // Build query params with organizationId
    const queryParams = {
      // Convert frontend 1-based page to backend 0-based page
      page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0,
      perPage: params?.perPage,
      search: params?.search,
      role: params?.role,
      organizationId: params?.organizationId
    };

    console.log('👥 Fetching users from API:', API_ENDPOINTS.USERS, queryParams);
    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<User>>>(
        API_ENDPOINTS.USERS,
        { params: queryParams }
      );

      console.log('👥 Users API Response:', {
        success: response.data.success,
        total: response.data.data?.total,
        dataLength: response.data.data?.data?.length,
        sampleUser: response.data.data?.data?.[0]
      });

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },

  async searchUser(query: string): Promise<ApiResponse<User>> {
    if (useMock) {
      console.log('👥 Using MOCK data for user search');
      // For mock, search in mockUsers data
      return mockUsers.getUsers({ search: query }).then(response => {
        if (response.success && response.data?.data && response.data.data.length > 0) {
          return {
            success: true,
            data: response.data.data[0]
          };
        }
        return {
          success: false,
          error: 'User not found'
        };
      });
    }

    console.log('👥 Searching for user:', query);
    try {
      const response = await axiosInstance.get<ApiResponse<User>>(
        API_ENDPOINTS.USER_SEARCH,
        { params: { query } }
      );

      console.log('👥 User search response:', response.data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'User not found'
      };
    }
  },

  async createUser(data: Omit<User, 'id'>): Promise<ApiResponse<User>> {
    if (useMock) {
      return mockUsers.createUser(data);
    }

    try {
      // Extract organizationId to pass in URL if present
      const organizationId = data.organization_id || (data as any).organizationId;
      const endpoint = organizationId
        ? API_ENDPOINTS.USERS_BY_ORG(organizationId)
        : API_ENDPOINTS.USERS;

      // Prepare request body with organizationId (camelCase)
      const requestBody = { ...data };
      if (organizationId) {
        (requestBody as any).organizationId = organizationId;
        // Remove organization_id if it exists (backend expects camelCase)
        delete requestBody.organization_id;
      }

      console.log('👥 Creating user with endpoint:', endpoint, 'Body:', requestBody);

      // Note: organizationId is sent in BOTH URL and request body
      // URL: /users?organizationId=<uuid>
      // Body: requestBody object (includes organizationId)
      const response = await axiosInstance.post<ApiResponse<User>>(
        endpoint,
        requestBody
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      console.error('👥 Create user error:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create user'
      };
    }
  },

  async updateUser(id: number, data: Partial<User>): Promise<ApiResponse<User>> {
    if (useMock) {
      return mockUsers.updateUser(id, data);
    }

    try {
      const response = await axiosInstance.put<ApiResponse<User>>(
        API_ENDPOINTS.USER_DETAIL(id),
        data
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update user'
      };
    }
  },

  async deleteUser(id: number): Promise<ApiResponse<void>> {
    if (useMock) {
      return mockUsers.deleteUser(id);
    }

    try {
      const response = await axiosInstance.delete<ApiResponse<void>>(API_ENDPOINTS.USER_DETAIL(id));

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete user'
      };
    }
  }
};